const hasValue = (value) =>
  value !== undefined && value !== null && value !== "";

const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round = (value) => Math.round((value + Number.EPSILON) * 100) / 100;
const money = (value) => round(value).toFixed(2);

const idOf = (value) => {
  if (!value || typeof value !== "object") return value;
  if (hasValue(value.id)) return value.id;
  if (hasValue(value.value) && typeof value.value !== "object") {
    return value.value;
  }
  return value.value && value.value.id;
};

const feature = (context, name) =>
  Boolean(
    (context.gymFeaturesObject && context.gymFeaturesObject[name]) ||
      (context.features && context.features[name])
  );

const needsForm = (code, message, fields, details = {}) => ({
  outcome: "needs_form",
  type: "sale",
  reason: {
    code,
    message,
    ...(fields && fields.length ? { fields } : {}),
    ...details,
  },
});

const ready = (payload) => ({
  outcome: "ready",
  type: "sale",
  endpoint: "member/membership",
  method: "post",
  payload,
});

const asRows = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const missingRowFields = (rows, specs, prefix) => {
  const missing = [];
  rows.forEach((row, index) => {
    specs.forEach(({ key, valid = hasValue }) => {
      if (!valid(row && row[key])) missing.push(`${prefix}[${index}].${key}`);
    });
  });
  return missing;
};

const localDay = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

// Mirrors DateHelper.TimeClashChecker, including allowing a new package to
// begin on an existing membership's end date.
const datesClash = (start1, end1, start2, end2) => {
  const s1 = localDay(start1);
  const e1 = localDay(end1);
  const s2 = localDay(start2);
  const e2 = localDay(end2);
  if ([s1, e1, s2, e2].some((date) => date === null)) return false;
  if (s2 === e1) return false;
  return (
    (s1 >= s2 && s1 <= e2) ||
    (e1 >= s2 && e1 <= e2) ||
    (s1 <= s2 && e1 >= e2)
  );
};

const activeMembership = (membership) =>
  membership &&
  !membership.isVoided &&
  !membership.isAfterReturn &&
  membership.subscriptionStatus === "ACTIVE";

const restrictionFailure = (packages, member, context) => {
  if (!member || (!member.memberships && !member.relations)) return null;
  const gymId =
    context.gymId ||
    (context.loginUser && context.loginUser.gym) ||
    (context.gym && (context.gym.id || context.gym.gym));

  for (let index = 0; index < packages.length; index += 1) {
    const pack = packages[index];
    const targetId = idOf(pack.member);
    const target =
      String(targetId) === String(member.id)
        ? member
        : asRows(member.relations).find(
            (relation) => String(relation.id) === String(targetId)
          );
    if (!target) {
      return needsForm(
        "unresolved_assigned_member",
        "A package is assigned to a member outside the resolved member family.",
        [`packages[${index}].member`]
      );
    }
    const isMainMember = String(targetId) === String(member.id);
    if (isMainMember && feature(context, "multiplePackages")) continue;

    const endDate =
      new Date(pack.startDate).getTime() +
      (number(pack.duration) + (number(pack.extendDuration) || 0)) * 86400000;
    const overlapping = asRows(target.memberships).filter(
      (membership) =>
        activeMembership(membership) &&
        datesClash(
          membership.startDate,
          membership.endDate,
          pack.startDate,
          endDate
        )
    );
    const duplicate = overlapping.find(
      (membership) =>
        membership.package &&
        String(membership.package.id) === String(pack.id) &&
        !membership.package.isSinglePackage
    );
    if (duplicate) {
      return needsForm(
        "duplicate_active_membership",
        "The assigned member already has this package during the requested dates.",
        [`packages[${index}].startDate`],
        { packageId: pack.id, memberId: targetId }
      );
    }

    if (pack.planType === "GYM") {
      const poolOnly = pack.poolAccessType === "pool_only";
      const gymOverlap = overlapping.find((membership) => {
        if (!membership.package || membership.package.planType !== "GYM") {
          return false;
        }
        const samePoolType = isMainMember
          ? (membership.package.poolAccessType === "pool_only") === poolOnly
          : true;
        const membershipGym =
          membership.package.gym &&
          (membership.package.gym.gym || membership.package.gym.id);
        const effectiveGym = isMainMember
          ? membershipGym || membership.gym
          : membershipGym;
        return (
          samePoolType &&
          (!hasValue(gymId) || String(effectiveGym) === String(gymId))
        );
      });
      if (gymOverlap) {
        return needsForm(
          "overlapping_gym_membership",
          "The assigned member already has an overlapping gym membership.",
          [`packages[${index}].startDate`],
          { packageId: pack.id, memberId: targetId }
        );
      }
    }
  }
  return null;
};

const calculateTotals = (packages, products, tax, insuranceDetails) => {
  const divisor = 1 + tax / 100;
  let netPrice = 0;
  let discount = 0;
  let insuranceEligibleWithTax = 0;
  let nonInsuranceWithTax = 0;

  packages.forEach((pack) => {
    const base = number(pack.price) / divisor;
    const rowDiscount = base * ((number(pack.discount) || 0) / 100);
    const withTax = (base - rowDiscount) * divisor;
    netPrice += base;
    discount += rowDiscount;
    if (pack.supportInsurance) insuranceEligibleWithTax += withTax;
    else nonInsuranceWithTax += withTax;
  });
  products.forEach((product) => {
    const base = (number(product.price) * number(product.quantity)) / divisor;
    netPrice += base;
    discount += base * ((number(product.discount) || 0) / 100);
  });

  const totalPrice = round((netPrice - discount) * divisor);
  let insuredTotal;
  const copayment = insuranceDetails && number(insuranceDetails.Copayment);
  if (copayment && packages.some((pack) => pack.supportInsurance)) {
    insuredTotal =
      packages.length === 1 && products.length === 0
        ? round(totalPrice * (copayment / 100))
        : round(insuranceEligibleWithTax * (copayment / 100)) +
          nonInsuranceWithTax;
  }
  return { netPrice, discount, totalPrice, insuredTotal };
};

const calculateCredit = (due, availableCredit, creditToUse, physicalPayment) => {
  const available = Math.max(round(availableCredit), 0);
  const requested = Math.max(round(creditToUse), 0);
  const physical = Math.max(round(physicalPayment), 0);
  const creditUsed = Math.min(requested, available, due);
  const dueAfterCredit = Math.max(round(due - creditUsed), 0);
  const physicalApplied = Math.min(physical, dueAfterCredit);
  const advance = Math.max(round(physical - physicalApplied), 0);
  return {
    received: round(creditUsed + physicalApplied),
    remaining: Math.max(round(due - creditUsed - physicalApplied), 0),
    memberCredit: {
      creditToUse: creditUsed,
      newPhysicalPayment: physical,
      advancePayment: { amount: advance },
    },
  };
};

export function prepareSalePayload(fields = {}, context = {}) {
  const member = fields.member || context.member;
  const memberId = idOf(member);
  if (!hasValue(memberId)) {
    return needsForm(
      "missing_required_fields",
      "The sale requires a resolved member lookup.",
      ["member.id"]
    );
  }

  const packages = asRows(fields.packages).map((row) => ({
    ...(row && row.value && typeof row.value === "object" ? row.value : row),
    ...(row && row.sale ? row.sale : {}),
  }));
  const products = asRows(fields.products).map((row) => ({
    ...(row && row.value && typeof row.value === "object" ? row.value : row),
    ...(row && row.sale ? row.sale : {}),
  }));
  if (!packages.length && !products.length) {
    return needsForm(
      "missing_required_fields",
      "The sale requires at least one resolved package or product.",
      ["packages", "products"]
    );
  }

  const packageMissing = missingRowFields(
    packages,
    [
      { key: "id" },
      { key: "price", valid: (value) => number(value) !== null },
      { key: "duration", valid: (value) => number(value) !== null },
      { key: "startDate", valid: (value) => localDay(value) !== null },
    ],
    "packages"
  );
  const productMissing = missingRowFields(
    products,
    [
      { key: "id" },
      { key: "price", valid: (value) => number(value) !== null },
      {
        key: "quantity",
        valid: (value) => number(value) !== null && number(value) > 0,
      },
    ],
    "products"
  );
  const missing = packageMissing.concat(productMissing);
  if (missing.length) {
    return needsForm(
      "incomplete_sale_items",
      "Resolved sale items are missing values required by the manual form.",
      missing
    );
  }

  packages.forEach((pack) => {
    if (!hasValue(pack.member)) pack.member = memberId;
  });
  products.forEach((product) => {
    if (!hasValue(product.member)) product.member = memberId;
  });

  const assignmentMissing = [];
  packages.forEach((pack, index) => {
    if (!hasValue(pack.member)) assignmentMissing.push(`packages[${index}].member`);
    if (pack.extendDuration && !pack.reason) {
      assignmentMissing.push(`packages[${index}].reason`);
    }
    if (
      pack.planType === "pt" &&
      feature(context, "mandateAssignTrainer") &&
      !hasValue(idOf(pack.assignedTrainer))
    ) {
      assignmentMissing.push(`packages[${index}].assignedTrainer`);
    }
  });
  if (assignmentMissing.length) {
    return needsForm(
      "missing_required_fields",
      "The sale item assignments do not satisfy the manual form.",
      assignmentMissing
    );
  }

  const restricted = restrictionFailure(packages, member, context);
  if (restricted) return restricted;

  const paymentType = fields.paymentType || "L";
  let paymentStatus = fields.paymentStatus || "F";
  const tax = hasValue(fields.tax)
    ? number(fields.tax)
    : number(context.gym && context.gym.taxRate) || 0;
  if (tax === null || tax < 0) {
    return needsForm("invalid_tax", "Tax must be a non-negative number.", ["tax"]);
  }
  if (!["L", "CC", "BA", "SP", "CO", "CQ"].includes(paymentType)) {
    return needsForm(
      "invalid_payment_type",
      "The payment type is not supported by the manual sale form.",
      ["paymentType"]
    );
  }
  if (paymentType === "CO" && feature(context, "addReasonForComplimantry")) {
    if (!fields.complimantryReason) {
      return needsForm(
        "missing_required_fields",
        "A complimentary reason is required by the gym configuration.",
        ["complimantryReason"]
      );
    }
  }

  const insuranceDetails =
    fields.insuranceDetails ||
    (member.metaData && member.metaData.insuranceDetails);
  const totals = calculateTotals(packages, products, tax, insuranceDetails);
  const payable =
    totals.insuredTotal !== undefined ? totals.insuredTotal : totals.totalPrice;

  if (
    feature(context, "addReasonForComplimantry") &&
    totals.discount > 0 &&
    !fields.discountReason
  ) {
    return needsForm(
      "missing_required_fields",
      "A discount reason is required by the gym configuration.",
      ["discountReason"]
    );
  }

  const joiningFeesEnabled = feature(context, "joiningFees");
  if (joiningFeesEnabled && packages.length && context.memberPaidJoiningFees === undefined) {
    return needsForm(
      "missing_context",
      "Whether the member has paid the joining fee must be resolved first.",
      ["context.memberPaidJoiningFees"]
    );
  }
  let joiningFees =
    joiningFeesEnabled &&
    packages.length &&
    context.memberPaidJoiningFees === false
      ? number(
          hasValue(fields.joiningFees)
            ? fields.joiningFees
            : context.gym && context.gym.joiningFees
        )
      : null;
  if (joiningFeesEnabled && packages.length && context.memberPaidJoiningFees === false && joiningFees === null) {
    return needsForm(
      "missing_context",
      "The gym joining fee amount is required for this member.",
      ["joiningFees"]
    );
  }

  let split = asRows(fields.split).map((item) => ({ ...item }));
  if (paymentType === "SP") {
    const splitMissing = missingRowFields(
      split,
      [
        { key: "type" },
        { key: "amount", valid: (value) => number(value) !== null && number(value) >= 0 },
      ],
      "split"
    );
    if (!split.length) splitMissing.push("split");
    if (splitMissing.length) {
      return needsForm(
        "incomplete_split_payment",
        "Split payment rows require a type and non-negative amount.",
        splitMissing
      );
    }
    if (joiningFees) {
      const feeIndex = split.findIndex(
        (item) => number(item.amount) >= joiningFees
      );
      if (feeIndex < 0) {
        return needsForm(
          "joining_fee_payment_required",
          "One split payment must cover the joining fee.",
          ["split"]
        );
      }
      split[feeIndex].amount = number(split[feeIndex].amount) - joiningFees;
    }
    if (products.length > 1) {
      return needsForm(
        "unsupported_split_payment",
        "The manual form does not support split payment for multiple products.",
        ["paymentType", "products"]
      );
    }
    const splitTotal = round(
      split.reduce((sum, item) => sum + number(item.amount), 0)
    );
    if (products.length === 1 && splitTotal !== totals.totalPrice) {
      return needsForm(
        "invalid_split_total",
        "A single-product split must exactly equal the sale total.",
        ["split"]
      );
    }
    if (splitTotal > totals.totalPrice) {
      return needsForm(
        "invalid_split_total",
        "The split total cannot exceed the sale total.",
        ["split"]
      );
    }
  } else {
    split = [];
  }

  const creditEnabled = Boolean(
    context.memberCreditEnabled ||
      (context.gymConfig &&
        context.gymConfig.manageMemberships &&
        context.gymConfig.manageMemberships.memberCredit &&
        context.gymConfig.manageMemberships.memberCredit.enabled)
  );
  let received = hasValue(fields.receivedAmount)
    ? number(fields.receivedAmount)
    : paymentType === "CO"
    ? 0
    : payable;
  let remaining = hasValue(fields.remainingAmount)
    ? number(fields.remainingAmount)
    : Math.max(round(payable - received), 0);
  let memberCredit;
  if (creditEnabled && hasValue(fields.creditToUse) && number(fields.creditToUse) > 0) {
    if (packages.length !== 1 || products.length) {
      return needsForm(
        "member_credit_not_applicable",
        "Member credit applies only to a single-package sale.",
        ["creditToUse"]
      );
    }
    if (number(context.memberCreditBalance) === null) {
      return needsForm(
        "missing_context",
        "The member credit balance must be resolved before using credit.",
        ["context.memberCreditBalance"]
      );
    }
    const physical =
      paymentType === "SP"
        ? split.reduce((sum, item) => sum + number(item.amount), 0)
        : number(fields.physicalPayment) || 0;
    if (number(fields.creditToUse) < 0 || physical < 0) {
      return needsForm(
        "invalid_payment_amount",
        "Credit and physical payment amounts cannot be negative.",
        ["creditToUse", "physicalPayment"]
      );
    }
    const credit = calculateCredit(
      payable,
      number(context.memberCreditBalance),
      number(fields.creditToUse),
      physical
    );
    received = credit.received;
    remaining = credit.remaining;
    memberCredit = credit.memberCredit;
    paymentStatus = remaining === 0 ? "F" : "P";
  }
  if (received === null || remaining === null || received < 0 || remaining < 0) {
    return needsForm(
      "invalid_payment_amount",
      "Received and remaining amounts must be non-negative numbers.",
      ["receivedAmount", "remainingAmount"]
    );
  }
  if ((packages.length !== 1 || products.length) && paymentStatus === "P") {
    return needsForm(
      "partial_payment_not_applicable",
      "The manual form permits partial payment only for one package and no products.",
      ["paymentStatus"]
    );
  }

  if (
    products.length &&
    fields.assignSeller !== "M" &&
    !hasValue(idOf(fields.assignedUser))
  ) {
    return needsForm(
      "missing_required_fields",
      "Product sales require an assigned seller unless the member is the seller.",
      ["assignedUser"]
    );
  }

  const requiresAttachment = packages.some((pack) => Boolean(pack.requiredAttachment));
  const hasExistingIdDocument =
    context.hasExistingIdDocument === true ||
    asRows(member.documents).some(
      (document) =>
        document.attachmentSource === "ID" || document.documentSource === "ID"
    );
  if (
    requiresAttachment &&
    !hasExistingIdDocument &&
    !hasValue(fields.attachment)
  ) {
    return needsForm(
      "identity_document_required",
      "The manual flow must upload or verify the member identity document.",
      ["attachment"]
    );
  }

  const packagePayload = packages.map((pack) => {
    const base = number(pack.price) / (1 + tax / 100);
    const discountPercent = number(pack.discount) || 0;
    const discountAmount = (base * discountPercent) / 100 || 0;
    const total = round((base - discountAmount) * (1 + tax / 100));
    const insured =
      totals.insuredTotal !== undefined &&
      pack.supportInsurance &&
      insuranceDetails &&
      number(insuranceDetails.Copayment)
        ? round(total * (number(insuranceDetails.Copayment) / 100))
        : undefined;
    const complimentary = paymentType === "CO";
    return {
      id: pack.id,
      planType: pack.planType,
      member: idOf(pack.member),
      price: complimentary ? 0 : number(pack.price),
      discount: complimentary ? "100" : discountPercent,
      startDate: new Date(pack.startDate).getTime(),
      sessionCount: pack.sessionCount,
      assignedTrainer: hasValue(idOf(pack.assignedTrainer))
        ? parseInt(idOf(pack.assignedTrainer), 10)
        : null,
      extendDuration: pack.extendDuration,
      reason: pack.reason,
      followupProgram: hasValue(idOf(pack.followupProgram))
        ? parseInt(idOf(pack.followupProgram), 10)
        : null,
      numberOfPlayers: hasValue(pack.numberOfPlayers)
        ? parseInt(pack.numberOfPlayers, 10)
        : null,
      supportInsurance: pack.supportInsurance,
      isRotationChanged: pack.isRotationChanged,
      ...(pack.pricingRuleId != null ? { pricingRuleId: pack.pricingRuleId } : {}),
      discountAmount: complimentary ? number(pack.price) : discountAmount,
      recievedAmount: complimentary
        ? 0
        : paymentStatus === "F"
        ? money(insured !== undefined ? insured : total)
        : received,
      ...(insured !== undefined
        ? {
            totalAmountAfterInsuranceCoverage: insured,
            insuranceCoverage: total - insured,
          }
        : {}),
      totalPrice: complimentary ? 0 : money(total),
      netPrice: complimentary ? 0 : base,
      ...(complimentary || paymentStatus === "F" ? { remainingAmount: 0 } : {}),
    };
  });

  const productPayload = products.map((product) => ({
    id: product.id,
    member: memberId,
    price: paymentType === "CO" ? 0 : number(product.price),
    quantity: number(product.quantity),
    availableInStock: product.availableInStock,
    discount:
      paymentType === "CO" ? "100" : number(product.discount) || 0,
  }));

  const draftFields = { ...fields };
  delete draftFields.member;
  delete draftFields.creditToUse;
  delete draftFields.physicalPayment;
  delete draftFields.packages;
  delete draftFields.products;
  const payload = {
    ...draftFields,
    user: {
      label: member.name || member.label || "",
      value: memberId,
    },
    packages: packagePayload,
    products: productPayload,
    selectedSaleServices: [
      ...(packages.length
        ? [{ value: 1, label: "Subscription", show: true }]
        : []),
      ...(products.length ? [{ value: 2, label: "Product", show: true }] : []),
    ],
    paymentType,
    paymentStatus,
    tax,
    split,
    netPrice: totals.netPrice,
    discount: totals.discount,
    totalPrice: money(totals.totalPrice),
    receivedAmount: money(received),
    remainingAmount: money(remaining),
    requiredAttachment: requiresAttachment,
    attachment: fields.attachment || "",
    assignSeller: fields.assignSeller || "",
    assignedUser:
      fields.assignSeller === "M" ? null : idOf(fields.assignedUser) || "",
    hasDisount: totals.discount > 0,
    joiningFees,
    cardProcessingFees:
      number(
        hasValue(context.cardProcessingFees)
          ? context.cardProcessingFees
          : context.gym && context.gym.cardProcessingFees
      ) || 0,
    ...(insuranceDetails ? { insuranceDetails } : {}),
    ...(totals.insuredTotal !== undefined
      ? { totalAmountAfterInsuranceCoverage: totals.insuredTotal }
      : {}),
    ...(memberCredit ? { memberCredit } : {}),
  };
  if (joiningFees && paymentType === "SP") {
    const source = asRows(fields.split).find(
      (item) => number(item.amount) >= joiningFees
    );
    payload.joiningPayment = source && source.type;
  }
  return ready(payload);
}

export async function execute(fields = {}, context = {}, dependencies = {}) {
  const prepared = prepareSalePayload(fields, context);
  if (prepared.outcome !== "ready") return prepared;
  const api = dependencies.api || context.api;
  if (typeof api !== "function") {
    return needsForm(
      "api_not_injected",
      "A sale API function must be injected before execution.",
      ["api"]
    );
  }

  const request = () =>
    api(prepared.endpoint, prepared.payload, prepared.method);
  const response =
    typeof (dependencies.dispatch || context.dispatch) === "function" &&
    dependencies.dispatchRequest === true
      ? await (dependencies.dispatch || context.dispatch)(request)
      : await request();
  return {
    outcome: "executed",
    type: "sale",
    endpoint: prepared.endpoint,
    method: prepared.method,
    payload: prepared.payload,
    response,
  };
}

export const saleCommand = { preparePayload: prepareSalePayload, execute };

export default saleCommand;
